import React, { useCallback, useEffect, useState } from 'react';
import { Activity, ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/admin/Sidebar';
import Header from '../../components/admin/Header';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/helpers';
import './AuditLogPage.css';

const PAGE_SIZE = 50;

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/audit-logs', {
        params: { page, limit: PAGE_SIZE, search, from: from || undefined, to: to || undefined }
      });
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [from, page, search, to]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  return (
    <div className="page-layout">
      <Sidebar />
      <div className="main-content">
        <Header
          title="Audit Log"
          subtitle="Review system activity and see who made each change."
          actions={(
            <button className="audit-refresh-button" onClick={fetchLogs} disabled={loading} type="button">
              <RefreshCw size={16} className={loading ? 'audit-spin' : ''} />
              Refresh
            </button>
          )}
        />
        <div className="page-body audit-log-page">
          <form className="audit-filters" onSubmit={applyFilters}>
            <label className="audit-search">
              <span>Search activity</span>
              <div className="audit-search-input">
                <Search size={16} />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Person, action, role or endpoint"
                />
              </div>
            </label>
            <label>
              <span>From</span>
              <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </label>
            <button type="submit" className="btn btn-primary">Apply filters</button>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear</button>
          </form>

          <section className="card audit-table-card">
            <div className="audit-table-heading">
              <div>
                <h2>Activity history</h2>
                <p>{total.toLocaleString()} recorded {total === 1 ? 'activity' : 'activities'}</p>
              </div>
              <Activity size={20} />
            </div>
            <div className="audit-table-scroll">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>Date and time</th>
                    <th>Who</th>
                    <th>Role</th>
                    <th>Activity</th>
                    <th>Target</th>
                    <th>Endpoint</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="audit-empty">Loading audit activity…</td></tr>
                  ) : logs.length === 0 ? (
                    <tr><td colSpan="7" className="audit-empty">No activity found for these filters.</td></tr>
                  ) : logs.map((log) => (
                    <tr key={log._id}>
                      <td className="audit-timestamp">{formatDateTime(log.createdAt)}</td>
                      <td>
                        <strong>{log.actorName}</strong>
                        {log.actorEmail && <span className="audit-secondary">{log.actorEmail}</span>}
                      </td>
                      <td><span className={`audit-role audit-role--${log.actorType}`}>{log.actorRole || log.actorType}</span></td>
                      <td>{log.action}</td>
                      <td className="audit-target">
                        <span>{log.resourceName || log.resourceId || log.resourceType}</span>
                        {log.stage && <span className="audit-secondary">Stage {log.stage}</span>}
                      </td>
                      <td><code>{log.method} {log.endpoint}</code></td>
                      <td><span className="audit-result">{log.statusCode}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="audit-pagination">
              <span>Page {totalPages ? page : 0} of {totalPages}</span>
              <div>
                <button type="button" aria-label="Previous page" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1 || loading}>
                  <ChevronLeft size={17} />
                </button>
                <button type="button" aria-label="Next page" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages || loading}>
                  <ChevronRight size={17} />
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
